import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";

const WORDS = ["Maple", "River", "Sunny", "Cedar", "Brook", "Lemon", "Tiger", "Ocean", "Piano", "Garden", "Harbor", "Meadow", "Willow", "Orange",
  "Silver", "Forest", "Summer", "Winter", "Spring", "Autumn", "Pepper", "Canyon", "Island", "Planet", "Rocket", "Violet", "Copper", "Falcon",
  "Breeze", "Candle", "Mango", "Peach", "Olive", "Coral", "Amber", "Daisy", "Tulip", "Robin", "Eagle", "Otter", "Panda", "Koala", "Zebra",
  "Comet", "Lunar", "Solar", "Prairie", "Valley", "Sunset", "Morning", "Melody", "Rainbow", "Thunder", "Pebble", "Lantern", "Kettle", "Basket"];

export const genPassword = () => {
  const n = (m) => crypto.getRandomValues(new Uint32Array(1))[0] % m;
  for (;;) {
    const a = WORDS[n(WORDS.length)], b = WORDS[n(WORDS.length)];
    const pw = `${a}-${b}-${10 + n(90)}`;
    if (a !== b && pw.length >= 15) return pw;
  }
};

export const fmtCell = (p) => (p ? `(${p.slice(2, 5)}) ${p.slice(5, 8)}-${p.slice(8)}` : "");
export const cellError = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (!d) return "";
  const n = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  return n.length === 10 && !"01".includes(n[0]) ? "" : "Enter a 10-digit US cell number";
};

export function CellPhoneEdit({ m, onSaved }) {
  const [v, setV] = useState(null);
  const err = v === null ? "" : cellError(v);
  const save = async () => {
    try { await api.patch(`/practice/members/${m.id}/phone`, { phone: v }); toast.success("Cell phone saved"); setV(null); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (v === null) return <button type="button" className="block text-slate-500 underline-offset-2 hover:underline" onClick={() => setV(m.phone ? fmtCell(m.phone) : "")} data-testid={`member-phone-${m.user_id}`}>{m.phone ? fmtCell(m.phone) : "+ add cell"}</button>;
  return (
    <div className="flex items-center gap-1 mt-1">
      <Input value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-36 text-xs" placeholder="555-123-4567" data-testid={`member-phone-input-${m.user_id}`} />
      <Button size="sm" className="h-7 btn-brand" disabled={!!err} onClick={save} data-testid={`member-phone-save-${m.user_id}`}>Save</Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => setV(null)}>Cancel</Button>
      {err && <span className="text-red-600 text-[11px]">{err}</span>}
    </div>
  );
}

const MAIL = { sent: "A sign-in email was sent (no password in it).", failed: "The sign-in email could not be sent - tell them yourself.", skipped: "" };

export function TempPassword({ value, onChange, testid }) {
  const copy = () => navigator.clipboard?.writeText(value).then(() => toast.success("Copied")).catch(() => {});
  return (
    <div className="flex gap-1">
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono" data-testid={testid} />
      <Button type="button" variant="outline" size="icon" onClick={() => onChange(genPassword())} title="Generate" data-testid={`${testid}-generate`}><RefreshCw className="w-4 h-4" /></Button>
      <Button type="button" variant="outline" size="icon" onClick={copy} title="Copy" data-testid={`${testid}-copy`}><Copy className="w-4 h-4" /></Button>
    </div>
  );
}

const L = ({ t, children }) => <div><p className="text-xs text-slate-500 mb-1">{t}</p>{children}</div>;

let sharedPw = null;
export const fetchSharedPw = (force) => {
  if (force || !sharedPw) sharedPw = api.get("/practice/temp-password").then((r) => r.data.password).catch(() => genPassword());
  return sharedPw;
};

export function TempPasswordSetting() {
  const [v, setV] = useState("");
  const [saved, setSaved] = useState("");
  useEffect(() => { fetchSharedPw(true).then((p) => { setV(p); setSaved(p); }); }, []);
  const save = async () => {
    try { await api.put("/practice/temp-password", { password: v }); fetchSharedPw(true); setSaved(v); toast.success("Shared temporary password saved for this organization"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-xl p-5 space-y-2" data-testid="temp-password-setting">
      <h3 className="font-semibold">Shared temporary password</h3>
      <p className="text-xs text-slate-500">Pre-filled for new staff accounts, password resets and the bulk members import in this organization.</p>
      <Input value={v} onChange={(e) => setV(e.target.value)} className="font-mono" data-testid="temp-password-input" />
      <p className="text-[11px] text-slate-500">15+ characters with a letter and a number. Everyone must choose their own password at first sign-in. Passwords found in public leak lists are refused by the sign-in service.</p>
      <Button size="sm" className="btn-brand" disabled={v === saved || v.length < 15} onClick={save} data-testid="temp-password-save">Save</Button>
    </div>
  );
}

export function MemberAddForm({ practice, roleList, label, onAdded, preset }) {
  const [mode, setMode] = useState("new");
  const [f, setF] = useState({ email: "", first_name: "", last_name: "", phone: "", password: "", role: "coordinator", provider_code: "__new", ...preset });
  const { reloadPractice } = usePractice();
  const [facs, setFacs] = useState([]);
  useEffect(() => { fetchSharedPw(true).then((p) => setF((x) => ({ ...x, password: x.password || p }))); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const ready = f.email && (mode === "existing" || (f.first_name && f.last_name && f.password.length >= 15 && !cellError(f.phone))) && (f.role !== "provider" || f.provider_code);
  const add = async () => {
    try {
      const prov = f.role === "provider";
      const body = { ...f, provider_code: prov ? f.provider_code : null, provider_facility_codes: prov && f.provider_code === "__new" ? facs : [] };
      const { data } = await api.post(mode === "new" ? "/practice/members/account" : "/practice/members", body);
      toast.success(`${mode === "new" ? `Account created. Give ${f.first_name} the temporary password - they must change it at first sign-in.` : "Member added."} ${f.role === "provider" && f.provider_code === "__new" ? (facs.length || practice.facilities.length === 1 ? "Provider record created. " : "Provider record created - add its facilities in Providers & facilities. ") : ""}${MAIL[data.email] || ""}`);
      setF({ ...f, email: "", first_name: "", last_name: "", phone: "", provider_code: "__new", password: await fetchSharedPw() }); setFacs([]); onAdded(); reloadPractice();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="member-add-form">
      <div className="flex gap-1 text-sm" role="tablist">
        {[["new", "Create new staff account"], ["existing", "Add existing account"]].map(([k, t]) => (
          <button key={k} onClick={() => setMode(k)} className={`px-3 py-1.5 rounded-md border transition-colors ${mode === k ? "bg-[var(--ace-navy)] text-white border-[var(--ace-navy)]" : "bg-white hover:bg-slate-50"}`} data-testid={`member-mode-${k}`}>{t}</button>))}
      </div>
      <p className="text-xs text-slate-500">{mode === "new" ? "Creates the sign-in with the organization's shared temporary password (you can type a different one). They get a sign-in email without the password and must choose their own password at first sign-in." : "For someone who already has an ACE eSIGN sign-in (for example a member of another organization)."}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end">
        {mode === "new" && <L t="First name"><Input value={f.first_name} onChange={set("first_name")} data-testid="member-first-input" /></L>}
        {mode === "new" && <L t="Last name"><Input value={f.last_name} onChange={set("last_name")} data-testid="member-last-input" /></L>}
        <L t="Email"><Input type="email" value={f.email} onChange={set("email")} data-testid="member-email-input" /></L>
        {mode === "new" && <L t="Cell phone (optional)"><Input type="tel" value={f.phone} onChange={set("phone")} placeholder="555-123-4567" data-testid="member-phone-input" />
          {cellError(f.phone) && <p className="text-[11px] text-red-600 mt-0.5" data-testid="member-phone-error">{cellError(f.phone)}</p>}</L>}
        {mode === "new" && <L t="Temporary password"><TempPassword value={f.password} onChange={set("password")} testid="member-password-input" /></L>}
        <L t="Role"><Select value={f.role} onValueChange={set("role")}><SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>{roleList.map((r) => <SelectItem key={r} value={r}>{label(r)}</SelectItem>)}</SelectContent></Select></L>
        {f.role === "provider" && <L t="Provider record"><Select value={f.provider_code} onValueChange={set("provider_code")}><SelectTrigger data-testid="member-provider-select"><SelectValue placeholder="Pick provider" /></SelectTrigger>
          <SelectContent><SelectItem value="__new" data-testid="member-provider-new">Create a new provider record from this person</SelectItem>
            {practice.providers.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent></Select></L>}
        {f.role === "provider" && f.provider_code === "__new" && practice.facilities.length > 1 && (
          <div className="sm:col-span-2 lg:col-span-4" data-testid="member-provider-facilities">
            <p className="text-xs font-medium mb-1">Facilities for the new provider</p>
            <div className="max-h-32 overflow-y-auto border rounded-md p-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-1">{practice.facilities.map((x) => (
              <label key={x.code} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={facs.includes(x.code)} data-testid={`member-provider-fac-${x.code}`}
                onChange={(e) => setFacs(e.target.checked ? [...facs, x.code] : facs.filter((c) => c !== x.code))} />{x.name}</label>))}</div>
          </div>)}
        <Button className="btn-brand" disabled={!ready} onClick={add} data-testid="member-add-btn">{mode === "new" ? "Create account" : "Add"} to {practice.short}</Button>
      </div>
    </div>
  );
}

export function ResetPasswordButton({ m }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const go = async () => {
    try { const { data } = await api.post(`/practice/members/${m.id}/reset-password`, { password: pw }); toast.success(`Password reset. ${m.name} must change it at next sign-in. ${MAIL[data.email] || ""}`); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={async () => { setPw(await fetchSharedPw()); setOpen(true); }} data-testid={`member-reset-pw-${m.user_id}`}><KeyRound className="w-3.5 h-3.5 mr-1" />Reset password</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password - {m.name}</DialogTitle>
            <DialogDescription>Signs {m.email} out everywhere. Give them this temporary password; they must choose a new one at sign-in.</DialogDescription></DialogHeader>
          <TempPassword value={pw} onChange={setPw} testid="reset-pw-input" />
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-brand" disabled={pw.length < 15} onClick={go} data-testid="reset-pw-confirm">Reset password</Button></div>
        </DialogContent>
      </Dialog>
    </>
  );
}
